import React, { useCallback, useEffect, useState } from "react";
import {
  fetchSuppliers, createSupplier, fetchIngredients,
  fetchPurchaseOrders, fetchPurchaseOrder, createPurchaseOrder,
  markPurchaseOrderOrdered, receivePurchaseOrder, cancelPurchaseOrder,
  createReorderDraftPOs, getCurrency, hasFeature,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Purchasing — suppliers + purchase orders. Create a PO (supplier + ingredient
// lines), mark it ordered, then receive it (records actual quantities and
// refreshes each ingredient's cost to the latest price).
const S = {
  en: {
    title: "Purchasing", sub: "Order stock from suppliers and receive it — receiving updates ingredient costs to the latest price.",
    new_po: "New purchase order", supplier: "Supplier", none: "— none —", add_supplier: "Add supplier",
    sup_name: "Supplier name", phone: "Phone", save: "Save", ingredient: "Ingredient", qty: "Qty",
    unit_price: "Unit price", line_total: "Line total", add_line: "+ Add line", note: "Note", expected: "Expected date",
    save_draft: "Save draft", total: "Total", status: "Status", mark_ordered: "Mark ordered", receive: "Receive",
    cancel: "Cancel", received_qty: "Received", update_costs: "Update ingredient costs to these prices",
    confirm_receive: "Confirm receive", no_orders: "No purchase orders yet.", created: "Created",
    receive_in_bo: "Receive this order in Back Office → Receiving.",
    need_lines: "Add at least one line.", all: "All",
    st_draft: "Draft", st_ordered: "Ordered", st_received: "Received", st_cancelled: "Cancelled",
    low_stock: "Low stock", reorder_low: "Reorder low stock",
    reorder_done: "Created {n} draft purchase order(s) from reorder levels — review and send below.",
    reorder_none: "Nothing to reorder — stock is above reorder levels.",
    reorder_err: "Could not create suggested POs.",
  },
  am: {
    title: "ግዢ", sub: "ከአቅራቢዎች ዕቃ ያዙ እና ይረከቡ — መረከብ የግብዓት ዋጋን ወደ አዲሱ ያዘምናል።",
    new_po: "አዲስ የግዢ ትዕዛዝ", supplier: "አቅራቢ", none: "— የለም —", add_supplier: "አቅራቢ ጨምር",
    sup_name: "የአቅራቢ ስም", phone: "ስልክ", save: "አስቀምጥ", ingredient: "ግብዓት", qty: "ብዛት",
    unit_price: "የነጠላ ዋጋ", line_total: "ድምር", add_line: "+ መስመር ጨምር", note: "ማስታወሻ", expected: "የሚጠበቅበት ቀን",
    save_draft: "ረቂቅ አስቀምጥ", total: "ጠቅላላ", status: "ሁኔታ", mark_ordered: "ታዘዘ ብለህ ምልክት አድርግ", receive: "ተረከብ",
    cancel: "ሰርዝ", received_qty: "የተረከበ", update_costs: "የግብዓት ዋጋን በእነዚህ ዋጋዎች አዘምን",
    confirm_receive: "መረከብ አረጋግጥ", no_orders: "እስካሁን የግዢ ትዕዛዝ የለም።", created: "የተፈጠረበት",
    receive_in_bo: "ይህን ትዕዛዝ በጀርባ ቢሮ → መቀበያ ይረከቡ።",
    need_lines: "ቢያንስ አንድ መስመር ጨምር።", all: "ሁሉም",
    st_draft: "ረቂቅ", st_ordered: "ታዟል", st_received: "ተረክቧል", st_cancelled: "ተሰርዟል",
    low_stock: "ዝቅተኛ ክምችት", reorder_low: "ዝቅተኛ ክምችት እንደገና አዝዝ",
    reorder_done: "ከዳግም-ማዘዣ ደረጃዎች {n} ረቂቅ የግዢ ትዕዛዝ ተፈጥሯል — ከታች ይገምግሙና ይላኩ።",
    reorder_none: "ማዘዝ የሚያስፈልግ የለም — ክምችት ከዳግም-ማዘዣ ደረጃ በላይ ነው።",
    reorder_err: "የተጠቆሙ የግዢ ትዕዛዞችን መፍጠር አልተቻለም።",
  },
  om: {
    title: "Bittaa", sub: "Dhiyeessitoota irraa meeshaa ajajii fudhadhu — fudhachuun gatii meeshaa gara haaraatti haaromsa.",
    new_po: "Ajaja bittaa haaraa", supplier: "Dhiyeessaa", none: "— hin jiru —", add_supplier: "Dhiyeessaa dabali",
    sup_name: "Maqaa dhiyeessaa", phone: "Bilbila", save: "Olkaa'i", ingredient: "Meeshaa", qty: "Baay'ina",
    unit_price: "Gatii tokkoo", line_total: "Ida'ama", add_line: "+ Sarara dabali", note: "Yaadannoo", expected: "Guyyaa eegamu",
    save_draft: "Wixinee olkaa'i", total: "Ida'ama", status: "Haala", mark_ordered: "Ajajame jedhii mallatteessi", receive: "Fudhadhu",
    cancel: "Haqi", received_qty: "Fudhatame", update_costs: "Gatii meeshaa gatiiwwan kanaan haaromsi",
    confirm_receive: "Fudhannaa mirkaneessi", no_orders: "Hanga ammaa ajaja bittaa hin jiru.", created: "Kan uumame",
    receive_in_bo: "Ajaja kana Waajjira Duubaa → Fudhannaa keessatti fudhadhu.",
    need_lines: "Yoo xiqqaate sarara tokko dabali.", all: "Hunda",
    st_draft: "Wixinee", st_ordered: "Ajajame", st_received: "Fudhatame", st_cancelled: "Haqame",
    low_stock: "Kuufama gadaanaa", reorder_low: "Kuufama gadaanaa irra deebi'ii ajaji",
    reorder_done: "Sadarkaa ajaja irra deebii irraa ajaja bittaa wixinee {n} uumame — gadiitti ilaalii ergi.",
    reorder_none: "Wanti ajajamu hin jiru — kuusaan sadarkaa ajaja irra deebii ol jira.",
    reorder_err: "Ajaja bittaa yaadame uumuu hin dandeenye.",
  },
};

const STATUS_COLORS = { draft: "#5f6b78", ordered: "#854F0B", received: "#1D9E75", cancelled: "#A32D2D" };

export default function PurchaseOrdersPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  // When Back Office is enabled it is the single place to receive goods, so the
  // legacy receive controls here are hidden (the backend also blocks the call).
  const bo = hasFeature("back_office");
  const cur = getCurrency() || "ETB";
  const money = (v) => formatPrice(v || 0, cur);

  const [suppliers, setSuppliers] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // new PO draft
  const [draft, setDraft] = useState({ supplier_id: "", note: "", expected_date: "", lines: [] });
  const [showNew, setShowNew] = useState(false);
  const [newSup, setNewSup] = useState({ name: "", phone: "" });

  // expanded PO + receive state
  const [openPo, setOpenPo] = useState(null);        // full PO object
  const [recv, setRecv] = useState({});              // lineId -> qty
  const [updateCosts, setUpdateCosts] = useState(true);

  const loadOrders = useCallback(() => {
    fetchPurchaseOrders(filter || undefined)
      .then(setOrders)
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load orders."));
  }, [filter]);

  useEffect(() => {
    fetchSuppliers().then(setSuppliers).catch(() => {});
    fetchIngredients().then(setIngredients).catch(() => {});
  }, []);
  useEffect(() => { loadOrders(); }, [loadOrders]);

  const ingById = (id) => ingredients.find((i) => String(i.id) === String(id));

  // ---- draft line editing ----
  const addLine = () => setDraft((d) => ({ ...d, lines: [...d.lines, { ingredient_id: "", quantity: 1, unit_price: 0 }] }));
  const setLine = (idx, patch) => setDraft((d) => {
    const lines = d.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l));
    return { ...d, lines };
  });
  const onPickIngredient = (idx, ingredient_id) => {
    const ing = ingById(ingredient_id);
    setLine(idx, { ingredient_id, unit_price: ing ? ing.unit_cost : 0 });
  };
  const removeLine = (idx) => setDraft((d) => ({ ...d, lines: d.lines.filter((_, i) => i !== idx) }));
  const draftTotal = draft.lines.reduce((a, l) => a + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);

  const saveDraft = async () => {
    const lines = draft.lines.filter((l) => l.ingredient_id && Number(l.quantity) > 0);
    if (!lines.length) { setErr(t("need_lines")); return; }
    setBusy(true); setErr("");
    try {
      await createPurchaseOrder({
        supplier_id: draft.supplier_id ? Number(draft.supplier_id) : null,
        note: draft.note || null,
        expected_date: draft.expected_date || null,
        lines: lines.map((l) => ({ ingredient_id: Number(l.ingredient_id), quantity: Number(l.quantity), unit_price: Number(l.unit_price) })),
      });
      setDraft({ supplier_id: "", note: "", expected_date: "", lines: [] });
      setShowNew(false);
      loadOrders();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };

  const addSupplier = async () => {
    if (!newSup.name.trim()) return;
    try {
      const s = await createSupplier({ name: newSup.name.trim(), phone: newSup.phone || null });
      setSuppliers((xs) => [...xs, s].sort((a, b) => a.name.localeCompare(b.name)));
      setDraft((d) => ({ ...d, supplier_id: String(s.id) }));
      setNewSup({ name: "", phone: "" });
    } catch (e) { setErr(e?.response?.data?.detail || "Could not add supplier."); }
  };

  // ---- PO actions ----
  const expand = async (id) => {
    if (openPo && openPo.id === id) { setOpenPo(null); return; }
    try {
      const po = await fetchPurchaseOrder(id);
      setOpenPo(po);
      const r = {}; (po.lines || []).forEach((l) => { r[l.id] = l.received_qty != null ? l.received_qty : l.quantity; });
      setRecv(r); setUpdateCosts(true);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not open."); }
  };
  const act = async (fn) => { setBusy(true); setErr(""); try { await fn(); setOpenPo(null); loadOrders(); } catch (e) { setErr(e?.response?.data?.detail || "Action failed."); } finally { setBusy(false); } };
  const doOrder = (po) => act(() => markPurchaseOrderOrdered(po.id));
  const doCancel = (po) => act(() => cancelPurchaseOrder(po.id));
  const doReceive = (po) => act(() => receivePurchaseOrder(po.id, {
    lines: (po.lines || []).map((l) => ({ line_id: l.id, received_qty: Number(recv[l.id]) || 0 })),
    update_costs: updateCosts,
  }));

  const lowStock = ingredients.filter((i) => i.reorder_level != null && (i.stock_on_hand || 0) <= i.reorder_level);
  // Server-side suggested POs: orders each low item up to its par level (or 2x
  // reorder as a default) and splits into one draft per preferred supplier.
  const reorderLowStock = async () => {
    setErr(""); setNote(""); setBusy(true);
    try {
      const r = await createReorderDraftPOs();
      loadOrders();
      setNote(r.po_count
        ? t("reorder_done").replace("{n}", r.po_count)
        : t("reorder_none"));
    } catch (e) { setErr(e?.response?.data?.detail || t("reorder_err")); }
    finally { setBusy(false); }
  };
  const counts = orders.reduce((a, o) => { a[o.status] = (a[o.status] || 0) + 1; return a; }, {});

  const inp = { padding: "7px 9px", borderRadius: 8, border: "1px solid #d6dede" };

  return (
    <div className="page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ marginBottom: 4 }}>🧾 {t("title")}</h1>
          <p className="muted" style={{ marginTop: 0, maxWidth: 640 }}>{t("sub")}</p>
        </div>
        <button className="btn" onClick={() => setShowNew((v) => !v)}>+ {t("new_po")}</button>
      </div>

      {/* ---- Summary strip ---- */}
      <div style={{ display: "flex", gap: 18, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
        <span className="muted" style={{ fontSize: 13 }}>{t("st_draft")}: <b style={{ color: "#063b48" }}>{counts.draft || 0}</b></span>
        <span className="muted" style={{ fontSize: 13 }}>{t("st_ordered")}: <b style={{ color: "#854F0B" }}>{counts.ordered || 0}</b></span>
        <span className="muted" style={{ fontSize: 13 }}>{t("low_stock")}: <b style={{ color: lowStock.length ? "#A32D2D" : "#1D9E75" }}>{lowStock.length}</b></span>
        {lowStock.length > 0 && (
          <button className="btn secondary" disabled={busy} onClick={reorderLowStock}>⟳ {t("reorder_low")} ({lowStock.length})</button>
        )}
      </div>

      {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
      {note && <div className="card" style={{ marginTop: 8, padding: 10, background: "#e7f6ec", borderColor: "#a9dfc0", color: "#1D7A44", fontSize: 13 }}>{note}</div>}

      {/* ---- New PO form ---- */}
      {showNew && (
        <div className="card" style={{ marginTop: 12, padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>{t("new_po")}</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <label className="muted" style={{ fontSize: 12 }}>{t("supplier")}</label><br />
              <select value={draft.supplier_id} onChange={(e) => setDraft((d) => ({ ...d, supplier_id: e.target.value }))} style={inp}>
                <option value="">{t("none")}</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="muted" style={{ fontSize: 12 }}>{t("expected")}</label><br />
              <input type="date" value={draft.expected_date} onChange={(e) => setDraft((d) => ({ ...d, expected_date: e.target.value }))} style={inp} />
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
              <input placeholder={t("sup_name")} value={newSup.name} onChange={(e) => setNewSup((s) => ({ ...s, name: e.target.value }))} style={{ ...inp, width: 130 }} />
              <input placeholder={t("phone")} value={newSup.phone} onChange={(e) => setNewSup((s) => ({ ...s, phone: e.target.value }))} style={{ ...inp, width: 100 }} />
              <button className="btn secondary" onClick={addSupplier}>{t("add_supplier")}</button>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            {draft.lines.map((l, idx) => (
              <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
                <select value={l.ingredient_id} onChange={(e) => onPickIngredient(idx, e.target.value)} style={{ ...inp, minWidth: 180 }}>
                  <option value="">{t("ingredient")}…</option>
                  {ingredients.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>)}
                </select>
                <input type="number" min="0" step="any" value={l.quantity} onChange={(e) => setLine(idx, { quantity: e.target.value })} style={{ ...inp, width: 80 }} title={t("qty")} />
                <input type="number" min="0" step="any" value={l.unit_price} onChange={(e) => setLine(idx, { unit_price: e.target.value })} style={{ ...inp, width: 100 }} title={t("unit_price")} />
                <span className="muted" style={{ minWidth: 90 }}>{money((Number(l.quantity) || 0) * (Number(l.unit_price) || 0))}</span>
                <button className="btn secondary" onClick={() => removeLine(idx)}>✕</button>
              </div>
            ))}
            <button className="btn secondary" onClick={addLine}>{t("add_line")}</button>
          </div>

          <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input placeholder={t("note")} value={draft.note} onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))} style={{ ...inp, flex: 1, minWidth: 160 }} />
            <b>{t("total")}: {money(draftTotal)}</b>
            <button className="btn" disabled={busy} onClick={saveDraft}>{t("save_draft")}</button>
          </div>
        </div>
      )}

      {/* ---- Filter ---- */}
      <div style={{ display: "flex", gap: 6, marginTop: 16, flexWrap: "wrap" }}>
        {["", "draft", "ordered", "received", "cancelled"].map((s) => (
          <button key={s || "all"} className={"chip" + (filter === s ? " active" : "")}
            onClick={() => setFilter(s)}
            style={{ padding: "5px 12px", borderRadius: 16, border: "1px solid #d6dede", background: filter === s ? "#063b48" : "#fff", color: filter === s ? "#fff" : "#333", cursor: "pointer", fontSize: 13 }}>
            {s ? t("st_" + s) : t("all")}
          </button>
        ))}
      </div>

      {/* ---- PO list ---- */}
      {orders.length === 0 ? (
        <div className="card" style={{ marginTop: 12, padding: 24, textAlign: "center" }}>
          <p className="muted" style={{ margin: 0 }}>{t("no_orders")}</p>
        </div>
      ) : (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {orders.map((po) => {
            const isOpen = openPo && openPo.id === po.id;
            return (
              <div key={po.id} className="card" style={{ padding: 0, overflow: "hidden" }}>
                <div onClick={() => expand(po.id)} style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
                    <b>#{po.id}</b>
                    <span>{po.supplier_name || t("none")}</span>
                    <span className="muted" style={{ fontSize: 12 }}>{po.line_count} · {t("created")} {(po.created_at || "").slice(0, 10)}</span>
                  </div>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <b>{money(po.total)}</b>
                    <span style={{ fontSize: 12, fontWeight: 700, color: STATUS_COLORS[po.status] || "#333" }}>{t("st_" + po.status)}</span>
                  </div>
                </div>

                {isOpen && (
                  <div style={{ padding: "0 16px 14px", borderTop: "1px solid #eef2f2" }}>
                    <table style={{ width: "100%", fontSize: 14, marginTop: 8 }}>
                      <tbody>
                        {(openPo.lines || []).map((l) => (
                          <tr key={l.id}>
                            <td style={{ padding: "4px 0" }}>{l.ingredient_name} <span className="muted" style={{ fontSize: 11 }}>({l.unit})</span></td>
                            <td style={{ textAlign: "right" }}>{l.quantity} × {money(l.unit_price)}</td>
                            <td style={{ textAlign: "right", width: 90 }}>{money(l.line_total)}</td>
                            {!bo && (po.status === "draft" || po.status === "ordered") && (
                              <td style={{ textAlign: "right", width: 110 }}>
                                <input type="number" min="0" step="any" value={recv[l.id] ?? ""} onChange={(e) => setRecv((r) => ({ ...r, [l.id]: e.target.value }))} style={{ ...inp, width: 90 }} title={t("received_qty")} />
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
                      {po.status === "draft" && <button className="btn secondary" disabled={busy} onClick={() => doOrder(po)}>{t("mark_ordered")}</button>}
                      {(po.status === "draft" || po.status === "ordered") && (
                        <>
                          {bo ? (
                            <span className="muted" style={{ fontSize: 12 }}>ℹ️ {t("receive_in_bo")}</span>
                          ) : (
                            <>
                              <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                                <input type="checkbox" checked={updateCosts} onChange={(e) => setUpdateCosts(e.target.checked)} /> {t("update_costs")}
                              </label>
                              <button className="btn" disabled={busy} onClick={() => doReceive(openPo)}>✓ {t("confirm_receive")}</button>
                            </>
                          )}
                          <button className="btn secondary" disabled={busy} onClick={() => doCancel(po)}>{t("cancel")}</button>
                        </>
                      )}
                      {po.note && <span className="muted" style={{ fontSize: 12 }}>📝 {po.note}</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
